import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { apiEndpoints, authToken, accessToken } from "../../config/config";
import { IoArrowUp } from "react-icons/io5";
import ScreenRecorder from "../Layout/ScreenRecorder";
import ScreenShot from "../Layout/ScreenShot";
import { useStaticTranslations } from "../../utils/staticTranslations"; 
import { useLanguage } from "../../contexts/LanguageContext";

const NewChat = ({
  conversationId,
  onBack,
  isInChatbot = false,
  isAiChat = false,
  customBranding,
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [newMessage, setNewMessage] = useState("");
  const [chatBotId, setChatBotId] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const isExpandedWindow = window.opener != null;
  const { currentLanguage } = useLanguage(); 
  const { commonMessage, buttonLabel } = useStaticTranslations(currentLanguage.code); 

  const maxMessageLength = 1000;
  const currentUserId = Number(localStorage.getItem("userId"));

  useEffect(() => {
    if (conversationId) {
      console.log("Fetching conversation with ID:", conversationId);
      fetchChatDetails(true);
    } else {
      // If no conversation ID, we're in a new chat
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!loading && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchChatDetails(false);
    }, 2000);

    return () => clearInterval(interval);
  }, [conversationId]);

  const fetchChatDetails = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(
        apiEndpoints.conversation_messages(conversationId),
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
            clientToken: `Bearer ${accessToken}`,
          },
        }
      );

      // Handle different response formats
      let messagesData;

      if (Array.isArray(response.data)) {
        messagesData = response.data;
      } else if (
        response.data.messages &&
        Array.isArray(response.data.messages)
      ) {
        messagesData = response.data.messages;
      } else {
        messagesData = [];
      }

      // sort messages by creation time before rendering
      messagesData.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      setMessages(messagesData);

      // If there are messages, get the chatBotId from the first message
      if (
        messagesData &&
        messagesData.length > 0 &&
        messagesData[0].chatBotId
      ) {
        setChatBotId(messagesData[0].chatBotId);
      } else {
        // Try to get chatBotId from localStorage as fallback
        const storedChatBotId = localStorage.getItem("chatBotId");
        if (storedChatBotId) {
          setChatBotId(storedChatBotId);
        } else {
          // Default value if nothing else is available
          setChatBotId(1);
        }
      }

      if (showLoading) setLoading(false);
    } catch (error) {
      console.error("Error fetching chat details:", error);
      if (showLoading) setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sendingMessage) return;

    try {
      // Set sending state to prevent double-sending
      setSendingMessage(true);

      // Store the message text before clearing input
      const messageToSend = newMessage.trim();

      // Clear input field immediately for better UX
      setNewMessage("");

      // Add temporary message to UI immediately
      const tempMessage = {
        id: "temp-" + Date.now(),
        conversation_id: conversationId,
        sender_id: currentUserId, // User ID (numeric)
        message: messageToSend,
        message_type: "text",
        attachment_url: null,
        chatBotId: chatBotId || localStorage.getItem("chatBotId") || 1,
        formId: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, tempMessage]);

      // Prepare API request data //test commit
      const messageData = {
        conversation_id: conversationId,
        sender_id: localStorage.getItem("userId"),
        message: messageToSend,
        message_type: "text",
        chatBotId: chatBotId || localStorage.getItem("chatBotId") || 1,
      };

      console.log("Sending message:", messageData);

      // Call the storeMessage API
      const storeResponse = await axios.post(
        apiEndpoints.storeMessage,
        messageData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
            clientToken: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("Message stored successfully:", storeResponse.data);

      // Update the temporary message with the actual message details if available
      if (storeResponse.data && storeResponse.data.id) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempMessage.id
              ? {
                  ...msg,
                  id: storeResponse.data.id,
                  created_at: storeResponse.data.created_at || msg.created_at,
                }
              : msg
          )
        );
      }

      // Check if we also need to handle bot response
      if (storeResponse.data && storeResponse.data.bot_response) {
        const botMessage = {
          id: storeResponse.data.bot_message_id || Date.now(),
          conversation_id: conversationId,
          sender_id: 2, // Bot ID
          message: storeResponse.data.bot_response,
          message_type: "text",
          attachment_url: null,
          chatBotId: chatBotId || localStorage.getItem("chatBotId") || 1,
          formId: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, botMessage]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Show error in UI if needed
      // You could remove the temporary message or mark it as failed
    } finally {
      setSendingMessage(false);
    }
  };

  // Determine if a message is from the chatbot or not
  const isFromChatbot = (message) => {
    return (
      message.sender_id === 2 ||
      message.sender_type === "bot" ||
      message.chatBotId === message.conversation_id
    );
  };

  // Handle back button click with direct state changes for robustness
  const handleBackClick = () => {
    console.log("Back button clicked in NewChat");

    // Check if onBack is available as a function and call it
    if (typeof onBack === "function") {
      console.log("Calling provided onBack function");
      onBack();
    } else {
      // Direct handling as fallback (access parent component's state via window object)
      console.log("No onBack function provided, using fallback");
      try {
        // Try to find the parent window/component to update its state
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            { type: "NEWCHAT_BACK_BUTTON_CLICKED" },
            "*"
          );
        }
      } catch (error) {
        console.error("Fallback navigation failed:", error);
      }
    }
  };

  // Wrapper element changes based on whether it's in chatbot or standalone
  const WrapperComponent = isInChatbot ? "div" : "div";
  const wrapperProps = isInChatbot
    ? { className: "flex flex-col h-full overflow-hidden bg-white" }
    : { className: "flex flex-col h-full overflow-hidden bg-white" };

  return (
    <WrapperComponent {...wrapperProps}>
      <div
        className={`border-b justify-between p-3 ${
          !isExpandedWindow ? "ml-3" : "ml-10 "
        } flex items-center bg-white`}
      >
        {/* {!isExpandedWindow && (
            <button
            onClick={handleBackClick}
            className="p-1 rounded-full hover:bg-gray-100 transition-all duration-300 mr-2"
            aria-label="Go back"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )} */}
        <div>
          <h2
            className={`font-bold text-lg ${
              !isExpandedWindow ? "" : "ml-auto mr-auto"
            }`}
          >
            {commonMessage('conversation')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <ScreenRecorder
            isExpandedWindow={isExpandedWindow}
            customBranding={customBranding}
            color="#2888b5"
          />
          <ScreenShot color="#2888b5" />
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-white"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">No messages in this conversation</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`p-3 rounded-lg max-w-[70%] flex gap-5 w-fit ${
                message.sender_id === currentUserId
                  ? "bg-blue-100 ml-auto text-right"
                  : "bg-gray-100"
              }`}
            >
              <p>{message.message}</p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(message.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          ))
        )}
      </div>

      <div
        className={`border-t p-3 pb-${!isExpandedWindow ? "0" : "2"} bg-white`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          className="rounded-b-lg"
        >
          <div className="relative mb-2">
            <div className="flex items-center bg-white shadow-md rounded-full border-slate-200 border-2 overflow-hidden">
              <input
                type="text"
                className="flex-grow p-3 pl-5 pr-8 text-gray-700 bg-transparent focus:outline-none text-sm"
                placeholder={buttonLabel('typeMessage')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sendingMessage}
                autoFocus
              />
              <div className="absolute right-12 top-8 transform -translate-y-1/2 text-xs text-gray-400">
                {maxMessageLength - newMessage.length}
              </div>
              <button
                type="submit"
                disabled={!newMessage.trim() || sendingMessage}
                className={`${
                  !newMessage.trim() || sendingMessage
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-500"
                } text-white p-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300`}
              >
                <IoArrowUp size={20} />
              </button>
            </div>
          </div>
        </form>
      </div>
    </WrapperComponent>
  );
};

export default NewChat;
